import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Container, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import api from '../../api/api';
import { useNavigate } from "react-router-dom";
import { getUserId } from "../../utils/auth";
import PageTitle from '../../components/common/PageTitle';

const AddFarmWrapper = ({ wizardData, completeStep, onNext, resetAllData, isWizardMode }) => {
  const { t, i18n } = useTranslation(); // Get i18n instance
  const navigate = useNavigate();
  const [validated, setValidated] = useState(false);
  
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const role = storedUser.role;
  const user_uid = storedUser.id;

  const [farmParams, setFarmParams] = useState({
    name: "",
    name_group: "",
    user_uid: "",
    city_id: ""
  });
  
  const [country, setCountry] = useState('');
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesLoadError, setCitiesLoadError] = useState(false);
  const [countries, setCountries] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Helper function to get country name based on current language
  const getCountryName = (countryObj) => {
    const lang = i18n.language; // Get current language (e.g., 'en', 'ar', 'fr')
    
    if (lang === 'ar' && countryObj.name_ar) {
      return countryObj.name_ar;
    } else if (lang === 'fr' && countryObj.name_fr) {
      return countryObj.name_fr;
    }
    return countryObj.name; // Default to English name
  };
  
  // Helper function to get city name based on current language
  const getCityName = (cityObj) => {
    const lang = i18n.language;
    
    // Return Arabic name if available and language is Arabic
    if (lang === 'ar' && cityObj.name_ar) {
      return cityObj.name_ar;
    }
    // Fall back to default city name
    return cityObj.city;
  };
  
  // Initialiser les données à partir du wizardData uniquement au montage
  useEffect(() => {
    if (wizardData?.step0?.data) {
      setFarmParams({
        name: wizardData.step0.data.name || "",
        name_group: wizardData.step0.data.name_group || "",
        user_uid: wizardData.step0.data.user_uid || "",
        city_id: wizardData.step0.data.city_id || ""
      });
      
      setCountry(wizardData.step0.data.country || '');
      setCities(wizardData.step0.data.cities || '');
    }
  }, []);
  
  // Vérifier si nous venons d'une soumission récente
  useEffect(() => {
    const wasRecentlySubmitted = localStorage.getItem('farmFormRecentlySubmitted');
    const resetAllForms = localStorage.getItem('resetAllForms');
    
    if (wasRecentlySubmitted || resetAllForms) {
      setFarmParams({
        name: "",
        name_group: "",
        user_uid: "",
        city_id: ""
      });
      setCountry("");
      setCities([]);
      
      localStorage.removeItem('farmFormRecentlySubmitted');
    }
  }, []);
  
  // Charger les données initiales
  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await api.get('/countries/get-countries');
        setCountries(res.data.Countries || []);
      } catch (error) {
        console.error("Error loading countries:", error);
      }
    };
    
    const getUsers = async () => {
      try {
        const res = await api.get('/admin/users');
        setUsers(res.data.users || []);
      } catch (error) {
        console.error("Error loading users:", error);
      }
    };

    getUsers();
    getCountries();
  }, []);

  // Load only the selected country's cities instead of downloading the
  // complete worldwide city list when the form opens.
  useEffect(() => {
    if (!country) {
      setCities([]);
      setCitiesLoadError(false);
      return;
    }

    let requestIsCurrent = true;

    const getCitiesByCountry = async () => {
      setCitiesLoading(true);
      setCitiesLoadError(false);

      try {
        const res = await api.get(`/cities/get-cities/${encodeURIComponent(country)}`);
        if (requestIsCurrent) {
          setCities(res.data.cities || []);
        }
      } catch (error) {
        console.error("Error loading cities for country:", error);
        if (requestIsCurrent) {
          setCities([]);
          setCitiesLoadError(true);
        }
      } finally {
        if (requestIsCurrent) {
          setCitiesLoading(false);
        }
      }
    };

    getCitiesByCountry();

    return () => {
      requestIsCurrent = false;
    };
  }, [country]);

  const handleCountryPick = (e) => {
    setCountry(e.target.value);
    setFarmParams((currentParams) => ({ ...currentParams, city_id: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Empêcher la double sauvegarde
    if (wizardData?.step0?.isCompleted) {
      onNext();
      return;
    }

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
      return;
    }
    
    const data = {
      ...farmParams,
      user_uid: role === "ROLE_ADMIN" ? farmParams.user_uid : user_uid
    };
    
    try {
      const response = await api.post('/farm/add-farm', data);
      if (response.data.type === "success") {
        const farmFormData = {
          ...farmParams,
          farmId: response.data.farm.uid,
          farmName: response.data.farm.name,
          country: country,
          cities: cities
        };
        
        localStorage.setItem('lastAddedFarmId', response.data.farm.uid);
        localStorage.setItem('lastAddedFarmName', response.data.farm.name);
        localStorage.setItem('farmFormRecentlySubmitted', 'true');
        
        swal({
          title: t("Farm Added"),
          text: t("Would you like to continue to create a field?"),
          icon: "success",
          buttons: [t("No"), t("Yes")]
        }).then((willContinue) => {
          completeStep(0, farmFormData);
          if (willContinue) {
            onNext();
          } else {
            const userId = getUserId();
            localStorage.removeItem(`smartFarmWizardData_${userId}`);
            localStorage.setItem('resetAllForms', 'true');
            
            const farmId = response.data.farm.uid;
            const farmName = response.data.farm.name;
            navigate('/add-field', { state: { farmId, farmName } });
          }
        });
      } else {
        swal(response.data.message || t("Something went wrong."), { icon: "error" });
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || t("An unknown error occurred.");
      swal(t("Error"), message, "error");
    }
  };

  return (
    <div className="wizard-step-content p-4">
      <h5 className="section-title">{t('farm_setup')}</h5>
      
      <Card className="wizard-intro-card bg-light border-0 rounded shadow-sm mb-4">
        <Card.Body>
          <p style={{ fontSize: "16px", lineHeight: "1.5", margin: "0" }}>
            {t("farm_introo")}
          </p>
        </Card.Body>
      </Card>
      
      <Form noValidate validated={validated} onSubmit={handleSubmit} className="wizard-step-form">
        <Row className="mb-3 gap-3 justify-content-between">
          <Form.Group as={Col} md="4" controlId="validationCustom01">
            <Form.Label>{t('name_farm')} *</Form.Label>
            <Form.Control
              required
              type="text"
              placeholder={t('name_farm')}
              value={farmParams.name}
              onChange={(e) => setFarmParams({ ...farmParams, name: e.target.value })}
              style={{ height: "40px" }}
            />
            <Form.Control.Feedback type="invalid">
              {t('provide_farm_name')}
            </Form.Control.Feedback>
            <Form.Control.Feedback>{t('looks_good')}</Form.Control.Feedback>
          </Form.Group>
          
          <Form.Group as={Col} md="3" controlId="validationCustom02">
            <Form.Label>{t('group_name')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t('group_name')}
              value={farmParams.name_group}
              onChange={(e) => setFarmParams({ ...farmParams, name_group: e.target.value })}
              style={{ height: "40px" }}
            />
            <Form.Control.Feedback>{t('looks_good')}</Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {t('provide_valid_group_name')}
            </Form.Control.Feedback>
          </Form.Group>
          
          <Form.Group as={Col} md="4" controlId="validationCustomUsername">
            <Form.Label>{t('select_country')} *</Form.Label>
            <Form.Select
              onChange={handleCountryPick}
              value={country}
              required
              style={{ height: "40px" }}
            >
              <option value="">{t('select_country')}</option>
              {countries.map(countryObj => (
                <option key={countryObj.id} value={countryObj.iso}>
                  {getCountryName(countryObj)}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {t('choose_country')}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        
        <Row className="mb-3 gap-3 justify-content-between">
          <Form.Group as={Col} md="6" controlId="validationCustom03" className="mt-2">
            <Form.Label>{t('select_city')} *</Form.Label>
            <Form.Select
              value={farmParams.city_id}
              onChange={e => setFarmParams({ ...farmParams, city_id: e.target.value })}
              required
              disabled={!country || citiesLoading || citiesLoadError}
              style={{ height: "40px" }}
            >
              <option value="">
                {citiesLoading
                  ? t('loading', { defaultValue: 'Loading...' })
                  : citiesLoadError
                    ? t('cities_load_error', { defaultValue: 'Unable to load cities' })
                    : t('select_city')}
              </option>
              {cities.length > 0 ? (
                cities.map(city => (
                  <option key={city.id} value={city.id}>{getCityName(city)}</option>
                ))
              ) : !country ? (
                <option disabled>{t('select_country_first', { defaultValue: 'Please select a country first' })}</option>
              ) : null}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {t('provide_valid_city')}
            </Form.Control.Feedback>
          </Form.Group>
          
          {role === "ROLE_ADMIN" && (
            <Form.Group as={Col} md="5" controlId="validationCustom03" className="mt-3">
              <Form.Label className="m-0">{t('select_user')} *</Form.Label>
              <Form.Select
                value={farmParams.user_uid}
                onChange={e => setFarmParams({ ...farmParams, user_uid: e.target.value })}
                required
                style={{ height: "40px" }}
              >
                <option value="">{t('select_user')}</option>
                {users && users.map(user => (
                  <option key={user.uid} value={user.uid}>{user.name}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {t('provide_valid_user')}
              </Form.Control.Feedback>
            </Form.Group>
          )}
        </Row>
        
        <div className="d-flex justify-content-end mt-4">
          <Button
            type="submit"
            variant="primary"
            className="px-4 py-2"
            disabled={citiesLoading || citiesLoadError}
          >
            {wizardData?.step0?.isCompleted ? (
              <>
                {t('next')} <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
              </>
            ) : (
              t('submit')
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddFarmWrapper;
